
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Button from '../../components/base/Button';

export default function Gallery() {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    // Update page title and meta description
    document.title = 'Painting Gallery - Atlanta Projects | WF & Y Painting LLC';
    
    // Add meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'View our painting project gallery featuring interior, exterior, and commercial painting work in Atlanta. See quality results from WF & Y Painting LLC.');

    // Add structured data for gallery page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/gallery#webpage`,
      "url": `${import.meta.env.VITE_SITE_URL}/gallery`,
      "name": "Painting Gallery - Atlanta Projects",
      "description": "View our painting project gallery featuring interior, exterior, and commercial painting work in Atlanta. See quality results from WF & Y Painting LLC.",
      "isPartOf": {
        "@type": "WebSite",
        "@id": `${import.meta.env.VITE_SITE_URL}/#website`
      },
      "mainEntity": {
        "@type": "ImageGallery",
        "name": "WF & Y Painting Project Gallery",
        "description": "Gallery showcasing completed painting projects in Atlanta including interior, exterior, and commercial work"
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const filters = [
    { id: 'all', label: 'All Projects' },
    { id: 'interior', label: 'Interior' },
    { id: 'exterior', label: 'Exterior' },
    { id: 'commercial', label: 'Commercial' },
    { id: 'residential', label: 'Residential' }
  ];

  const projects = [
    {
      id: 1,
      title: 'Modern Living Room Interior',
      category: 'interior',
      type: 'residential',
      image: 'https://public.readdy.ai/ai/img_res/ac9a27f858f895d55c3b5399d676bad9.jpg',
      description: 'Complete interior painting transformation with custom color consultation'
    },
    {
      id: 2,
      title: 'Residential Exterior Makeover',
      category: 'exterior',
      type: 'residential',
      image: 'https://readdy.ai/api/search-image?query=Beautiful%20house%20exterior%20with%20fresh%20professional%20paint%20job%2C%20modern%20residential%20home%2C%20attractive%20curb%20appeal%2C%20professional%20exterior%20painting%2C%20clean%20suburban%20setting&width=600&height=400&seq=gallery2&orientation=landscape',
      description: 'Full exterior painting with trim work and pressure washing preparation'
    },
    {
      id: 3,
      title: 'Office Building Commercial Project',
      category: 'exterior',
      type: 'commercial',
      image: 'https://readdy.ai/api/search-image?query=Modern%20office%20building%20exterior%20with%20fresh%20professional%20paint%2C%20commercial%20property%2C%20business%20district%2C%20professional%20commercial%20painting%2C%20Atlanta%20cityscape&width=600&height=400&seq=gallery3&orientation=landscape',
      description: 'Large-scale commercial exterior painting and maintenance project'
    },
    {
      id: 4,
      title: 'Kitchen Cabinet Refinishing',
      category: 'interior',
      type: 'residential',
      image: 'https://readdy.ai/api/search-image?query=Modern%20kitchen%20with%20professionally%20painted%20cabinets%2C%20beautiful%20cabinet%20refinishing%2C%20elegant%20kitchen%20design%2C%20premium%20paint%20finish%2C%20bright%20kitchen%20interior&width=600&height=400&seq=gallery4&orientation=landscape',
      description: 'Custom kitchen cabinet painting and refinishing service'
    },
    {
      id: 5,
      title: 'Historic Home Restoration',
      category: 'exterior',
      type: 'residential',
      image: 'https://readdy.ai/api/search-image?query=Historic%20Victorian%20house%20with%20restored%20exterior%20paint%2C%20classic%20home%20architecture%2C%20heritage%20property%20restoration%2C%20traditional%20painting%20techniques%2C%20Atlanta%20historic%20district&width=600&height=400&seq=gallery5&orientation=landscape',
      description: 'Careful restoration of historic home exterior with period-appropriate colors'
    },
    {
      id: 6,
      title: 'Retail Store Interior',
      category: 'interior',
      type: 'commercial',
      image: 'https://readdy.ai/api/search-image?query=Modern%20retail%20store%20interior%20with%20professional%20painting%2C%20commercial%20interior%20design%2C%20bright%20shopping%20space%2C%20professional%20commercial%20painting%2C%20clean%20business%20environment&width=600&height=400&seq=gallery6&orientation=landscape',
      description: 'Commercial interior painting for retail space transformation'
    },
    {
      id: 7,
      title: 'Luxury Home Master Bedroom',
      category: 'interior',
      type: 'residential',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20master%20bedroom%20with%20professional%20interior%20painting%2C%20luxury%20bedroom%20design%2C%20sophisticated%20color%20palette%2C%20premium%20wall%20finish%2C%20upscale%20home%20interior&width=600&height=400&seq=gallery7&orientation=landscape',
      description: 'High-end interior painting for luxury residential master suite'
    },
    {
      id: 8,
      title: 'Warehouse Exterior Coating',
      category: 'exterior',
      type: 'commercial',
      image: 'https://readdy.ai/api/search-image?query=Industrial%20warehouse%20with%20professional%20exterior%20coating%2C%20commercial%20building%20maintenance%2C%20industrial%20painting%20project%2C%20large-scale%20commercial%20work&width=600&height=400&seq=gallery8&orientation=landscape',
      description: 'Industrial exterior coating and protective painting services'
    },
    {
      id: 9,
      title: 'Home Office Transformation',
      category: 'interior',
      type: 'residential',
      image: 'https://readdy.ai/api/search-image?query=Modern%20home%20office%20with%20professional%20interior%20painting%2C%20productive%20workspace%20design%2C%20elegant%20office%20interior%2C%20premium%20paint%20finish%2C%20natural%20lighting&width=600&height=400&seq=gallery9&orientation=landscape',
      description: 'Complete home office interior painting and color design'
    }
  ];

  const filteredProjects = projects.filter(project => {
    if (activeFilter === 'all') return true;
    return project.category === activeFilter || project.type === activeFilter;
  });

  return (
    <div className="min-h-screen pt-8">
      {/* Header Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">
            Project Gallery
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Explore our portfolio of completed painting projects across Atlanta
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeFilter === filter.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group"
                onClick={() => setSelectedImage(project.image)}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-64 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                    <i className="ri-eye-line text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {project.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-blue-600 font-medium capitalize">
                      {project.category} • {project.type}
                    </span>
                    <button className="text-blue-600 hover:text-blue-700 transition-colors">
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">500+</div>
              <div className="text-gray-600">Projects Completed</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">15+</div>
              <div className="text-gray-600">Years Experience</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">100%</div>
              <div className="text-gray-600">Customer Satisfaction</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">24/7</div>
              <div className="text-gray-600">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Ready for Your Project?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Let us transform your space with professional painting services
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button variant="secondary" size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                <i className="ri-mail-fill"></i>
                {t('getQuote')}
              </Button>
            </Link>
            <a href="tel:770-875-6990">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600">
                <i className="ri-phone-fill"></i>
                770-875-6990
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <img
              src={selectedImage}
              alt="Gallery Image"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-all duration-300"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
